import {
  useGetInvoicesSuspense,
  useGetRolesSuspense,
  useGetUsersSuspense,
  usePostRoles,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { BillingToggle } from './BillingToggle';
import {
  ALL_ROLES_VALUE,
  FILLER_BORDER,
  GRID_CONTAINER,
  TILE_BORDER,
} from './constants';
import { CreateUserModal } from './CreateUserModal';
import { EmptyGridTile, GridTile } from './GridTile';
import { PaymentsModal } from './PaymentsModal';
import { RoleColorDot } from './RoleColorDot';

type UserRow = {
  id: string;
  externalId: string;
  role: string;
  invoiceCount: number;
};

const userSchema: ColumnDefinition<UserRow>[] = [
  {
    title: 'ID',
    colspan: 'col-span-4',
    format: (row) => (
      <div className="flex items-center gap-x-f1">
        <RoleColorDot role={row.role} />
        <span>{row.externalId}</span>
      </div>
    ),
  },
  {
    title: 'Invoices',
    justify: 'right',
    colspan: 'col-span-2',
    format: (row) => (
      <span>{row.invoiceCount > 0 ? String(row.invoiceCount) : '—'}</span>
    ),
  },
];

const ColumnHeaders = () => (
  <div className="grid grid-cols-6 gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-4">ID</div>
    <div className="col-span-2 text-right">Invoices</div>
  </div>
);

const ViewToggle = ({
  viewMode,
  setViewMode,
  readOnly,
}: {
  viewMode?: 'grid' | 'list';
  setViewMode?: (m: 'grid' | 'list') => void;
  readOnly?: boolean;
}) => (
  <BillingToggle
    readOnly={readOnly}
    initialActiveButton={viewMode === 'list' ? 'right' : 'left'}
    onLeftClick={() => setViewMode?.('grid')}
    onRightClick={() => setViewMode?.('list')}
    leftLabel={<Icon type="gridView" size="sm" />}
    rightLabel={<Icon type="listView" size="sm" />}
    buttonPadding="py-f0"
    buttonHeight="h-fit"
  />
);

const NewRoleModal = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (role: string) => void;
}) => {
  const [roleInput, setRoleInput] = useState('');
  const [createRoleError, setCreateRoleError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const createRoleMutation = usePostRoles(undefined, queryClient);
  const [isPending, setIsPending] = useState(false);
  const handleSubmit = useCallback(async () => {
    try {
      setIsPending(true);
      await createRoleMutation.mutateAsync({
        data: { role: roleInput.trim() },
      });
      await queryClient.invalidateQueries({ queryKey: ['/roles'] });
      onSubmit(roleInput.trim());
      setIsPending(false);
    } catch (err) {
      setIsPending(false);
      setCreateRoleError(
        err instanceof Error ? err.message : 'Failed to create role'
      );
    }
  }, [createRoleMutation, queryClient, onSubmit, roleInput]);
  return (
    <PaymentsModal
      width="sm"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      body={
        <div className="flex flex-col space-y-f4 font-sans">
          <span className="text-main text-md w-full text-left h-fit">
            {`Roles are categories of users and products like "Vendor", "Platform",
            "Customer", etc.`}
          </span>
          <ModalTextInput
            placeholder="Role name"
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
          />
        </div>
      }
      footer={
        <div className="flex flex-row justify-between">
          {createRoleError && <ErrorText>{createRoleError}</ErrorText>}
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            onCancel={onClose}
            submitButtonLabel={isPending ? 'Creating...' : 'Create'}
            disabled={isPending || !roleInput.trim()}
          />
        </div>
      }
    />
  );
};

const UserGridTile = ({ user, href }: { user: UserRow; href: string }) => (
  <GridTile
    href={href}
    icon={<RoleColorDot role={user.role} size={24} hoverAnimate />}
    primary={user.externalId}
  />
);

const EmptyUserGridTile = () => (
  <EmptyGridTile
    icon={<div className="w-f2 h-f2 rounded-full shrink-0 bg-main-200" />}
    label="No Users Yet"
  />
);

const userEmptyValue = {
  ID: 'No Users Yet',
  Invoices: '—',
};

const SKELETON_MAX_TILES = 12;

type Props = {
  workspaceId: string;
};

const UsersListPageLoading = ({ workspaceId: _workspaceId }: Props) => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Users"
      secondaryAction={{ title: 'Edit', onClick: () => {}, disabled: true }}
      primaryAction={{ title: 'New', onClick: () => {}, disabled: true }}
    >
      <div className="flex flex-col w-full min-h-0">
        <div className="sticky top-0 z-10 shrink-0">
          <div className="flex items-end w-full">
            <div className="flex-1 border-b-[0.5px] border-main-300 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
            <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
              <ViewToggle readOnly />
            </div>
          </div>
        </div>
        <div className={GRID_CONTAINER}>
          <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
          {/* eslint-disable react/no-array-index-key */}
          {Array.from({ length: visibleTiles }).map((__, i) => (
            <div
              key={`skeleton-${i}`}
              className={clsx('aspect-[8/9]', TILE_BORDER)}
            />
          ))}
          {Array.from({ length: 8 }).map((__, i) => (
            <div key={`filler-${i}`} className={FILLER_BORDER} />
          ))}
          {/* eslint-enable react/no-array-index-key */}
        </div>
      </div>
    </PaymentViewLayout>
  );
};

const UsersListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Users');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(ALL_ROLES_VALUE);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const { data: usersData } = useGetUsersSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const { data: rolesData } = useGetRolesSuspense();

  const roleOptions = useMemo(
    () => [
      { value: ALL_ROLES_VALUE, label: 'All' },
      ...rolesData.data
        .map((r) => ({ value: r.role, label: capitalize(r.role) }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    ],
    [rolesData.data]
  );

  const invoiceCountByUser = useMemo(() => {
    const counts: Record<string, number> = {};
    invoicesData.data.forEach((invoice) => {
      const userIds = new Set(
        (invoice.line_items ?? []).map((item) => item.user_id).filter(Boolean)
      );
      userIds.forEach((userId) => {
        counts[userId] = (counts[userId] ?? 0) + 1;
      });
    });
    return counts;
  }, [invoicesData.data]);

  const users: UserRow[] = useMemo(
    () =>
      usersData.data
        .filter(
          (u) => selectedRole === ALL_ROLES_VALUE || u.role === selectedRole
        )
        .map((u) => ({
          id: u.id,
          externalId: u.external_id,
          role: u.role,
          invoiceCount: invoiceCountByUser[u.external_id] ?? 0,
        })),
    [usersData.data, invoiceCountByUser, selectedRole]
  );

  const getPath = useCallback(
    (user: UserRow) =>
      makePath(['w', workspaceId, 'payments', 'users', user.externalId], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreate = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Users"
        secondaryAction={{
          title: 'Edit',
          onClick: () => {},
          disabled: selectedRole === ALL_ROLES_VALUE,
        }}
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'create-user-button',
        }}
      >
        <div className="flex flex-col w-full h-full min-h-0">
          <div className="sticky top-0 z-10 shrink-0">
            <div className="group flex items-end w-full">
              <TabsV2
                options={roleOptions}
                selectedValue={selectedRole}
                onClick={setSelectedRole}
                borderColor="main-300"
              />
              <button
                type="button"
                onClick={() => setShowNewRoleModal(true)}
                className="w-[2lh] min-w-[2lh] aspect-square flex items-center justify-center text-xmd text-main-500 border-b-[0.5px] border-r-[0.5px] border-main-300 hover:text-main hover:cursor-pointer"
              >
                <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                  +
                </span>
              </button>
              <div className="flex-1 border-b-[0.5px] border-main-300" />
              <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
                <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
              </div>
            </div>
            {viewMode === 'list' && <ColumnHeaders />}
          </div>
          {viewMode === 'list' ? (
            <NavTableV2
              textSize="xmd"
              data={users}
              emptyValue={userEmptyValue}
              getPath={getPath}
              schema={userSchema}
              showHeader={false}
              stickyHeader={false}
              rowHeight="fixed"
            />
          ) : (
            <div className={GRID_CONTAINER}>
              {users.length === 0 ? (
                <EmptyUserGridTile />
              ) : (
                users.map((user) => (
                  <UserGridTile
                    key={user.id}
                    user={user}
                    href={getPath(user)}
                  />
                ))
              )}
              {Array.from({ length: 8 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`filler-${i}`} className={FILLER_BORDER} />
              ))}
            </div>
          )}
        </div>
      </PaymentViewLayout>
      {showCreateModal && (
        <CreateUserModal
          onClose={handleCloseModal}
          onCreated={handleCloseModal}
        />
      )}
      {showNewRoleModal && (
        <NewRoleModal
          onClose={() => setShowNewRoleModal(false)}
          onSubmit={(roleName) => {
            setShowNewRoleModal(false);
            setSelectedRole(roleName);
          }}
        />
      )}
    </>
  );
};

export const UsersListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<UsersListPageLoading workspaceId={workspaceId} />}>
    <UsersListPageContent workspaceId={workspaceId} />
  </Suspense>
);
